// `ViewChips` (design-system §3.3, PR-E4) — the saved-views chip row. An
// i18n-free primitive: every word it shows arrives as a prop, translated by
// the caller, like `DataTable`'s `sort.toggleLabel`.
//
// A wrapping `role="group"` of `aria-pressed` buttons styled `.ds-viewchip`
// (lifted from production's `.view-chip`, with the pressed state read from
// `aria-pressed` rather than a class, so the state has one source) inside a
// `.ds-viewswitcher` row. A caller may pass runs of chips as labelled groups:
// each renders as a nested `role="group"` named by its label through
// `aria-labelledby`, carries `data-group` set to the group's key, shows the
// label as quiet text before its first chip, wraps with the row, and renders
// nothing when it holds no chip. The saved-views switcher passes one, the
// other members' shared views under "Shared" with `data-group="shared"`
// (spec 16 §4).
//
// `trailing` renders after the chips and groups (the switcher's More views
// chip, which opens a list and is not a toggle, so it carries no
// `aria-pressed`), and `children` after that: the quiet notes and the tool
// buttons, which `office.css` lays out as `.ds-viewswitcher-note` and
// `.ds-viewswitcher-tools`. Every rule renders at every width, and the row
// wraps at 412 px with no horizontal scroll.

import { useId, type ReactNode } from "react";

export interface ViewChip {
  /** Stable React key, and the chip's `data-view` attribute. */
  readonly key: string;
  readonly label: string;
  readonly pressed: boolean;
  readonly onPress: () => void;
  readonly testId?: string | undefined;
}

export interface ViewChipGroup {
  /** Written to `data-group`. */
  readonly key: string;
  /** The quiet label before the group's first chip, and its accessible name. */
  readonly label: string;
  readonly chips: readonly ViewChip[];
}

export interface ViewChipsProps {
  /** The row's accessible name. */
  readonly label: string;
  readonly chips: readonly ViewChip[];
  readonly groups?: readonly ViewChipGroup[] | undefined;
  readonly trailing?: ReactNode;
  readonly children?: ReactNode;
  readonly testId?: string | undefined;
}

function Chip({ chip }: { chip: ViewChip }) {
  return (
    <button
      type="button"
      className="ds-viewchip"
      aria-pressed={chip.pressed}
      data-view={chip.key}
      data-testid={chip.testId}
      onClick={chip.onPress}
    >
      {chip.label}
    </button>
  );
}

function Group({ group }: { group: ViewChipGroup }) {
  const labelId = useId();
  if (group.chips.length === 0) return null;
  return (
    <div
      className="ds-viewchip-group"
      role="group"
      aria-labelledby={labelId}
      data-group={group.key}
    >
      <span id={labelId} className="ds-viewchip-group-label">
        {group.label}
      </span>
      {group.chips.map((chip) => (
        <Chip key={chip.key} chip={chip} />
      ))}
    </div>
  );
}

export function ViewChips({ label, chips, groups, trailing, children, testId }: ViewChipsProps) {
  return (
    <div className="ds-viewswitcher" role="group" aria-label={label} data-testid={testId}>
      {chips.map((chip) => (
        <Chip key={chip.key} chip={chip} />
      ))}
      {(groups ?? []).map((group) => (
        <Group key={group.key} group={group} />
      ))}
      {trailing}
      {children}
    </div>
  );
}
